import type {
  FetchAjaxStubLogger,
  FetchTraceLogger,
} from './instrumentation';
import { createBoundedRequestTracker } from './telemetry';
import {
  getUrlPathname,
  joinFetchUrl,
} from './url';

export interface FetchTelemetryRequestOptions {
  headers: Record<string, string | number>;
  method?: string;
  body?: unknown;
}

export interface FetchTelemetryResponse {
  headers: {
    get: (name: string) => string | null;
  };
  url: string;
  ok: boolean;
  status?: number;
  statusText?: string | null;
  bodyText?: string;
}

export interface FetchTelemetryResponseLogMetadata {
  statusText: string | null;
  status: number;
  errorContext: string | null;
}

export interface CreateFetchTelemetryLifecycleOptions {
  ajaxStubLogger: FetchAjaxStubLogger;
  requestLogger: FetchTraceLogger;
  getTimestamp: () => number;
  getResponseLogMetadata: (
    response: FetchTelemetryResponse
  ) => FetchTelemetryResponseLogMetadata;
}

export interface FetchTelemetryLifecycle {
  beforeFetch: (
    options: FetchTelemetryRequestOptions,
    url: string,
    route: string
  ) => void;
  afterFetch: (response: FetchTelemetryResponse) => void;
}

export const getLegacyFetchResponseLogMetadata = (): FetchTelemetryResponseLogMetadata => ({
  statusText: '',
  status: -1,
  errorContext: '',
});

export const getHttpFetchResponseLogMetadata = (response: FetchTelemetryResponse): FetchTelemetryResponseLogMetadata => ({
  statusText: response.statusText || '',
  status: response.status ?? -1,
  errorContext: null,
});

export function createFetchTelemetryLifecycle(options: CreateFetchTelemetryLifecycleOptions): FetchTelemetryLifecycle {
  const inflight = createBoundedRequestTracker<{ httpMethod: string }>({
    getTimestamp: options.getTimestamp,
  });

  return {
    beforeFetch: (requestOptions, url, route) => {
      const httpMethod = requestOptions.method || 'GET';
      const requestId = requestOptions.headers['x-ms-requestid'] as string;
      inflight.track(requestId, { httpMethod });

      const path = joinFetchUrl(url, route);
      options.requestLogger.trace(
        { body: requestOptions.body },
        path,
        requestId,
        httpMethod
      );
      options.ajaxStubLogger.ajax(
        requestId,
        path,
        true,
        httpMethod,
        0,
        true,
        '',
        -1
      );
    },
    afterFetch: (response) => {
      const requestId = response.headers.get('x-ms-requestid') || '';
      const responsePathname = getUrlPathname(response.url);
      const entry = inflight.take(requestId);
      const duration = entry
        ? options.getTimestamp() - entry.startTime
        : 0;
      const httpMethod = entry?.value.httpMethod || 'GET';
      const metadata = options.getResponseLogMetadata(response);

      options.ajaxStubLogger.ajax(
        requestId,
        responsePathname,
        false,
        httpMethod,
        duration,
        response.ok,
        metadata.statusText,
        metadata.status
      );

      if (!response.ok) {
        const error = [
          `Ajax error [${response.status}]`,
          response.statusText || 'error',
          response.bodyText,
        ].filter(Boolean).join(', ');

        options.ajaxStubLogger.error(
          error,
          responsePathname,
          requestId,
          metadata.errorContext,
          httpMethod
        );
      }
    },
  };
}
